import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { toast } from 'sonner';
import { pathId } from '../../lib/pathId';
import { Calendar as CalendarIcon, Loader2, Plus, Pencil, Lock, Globe, Search, X, FileText, Building2, Newspaper } from 'lucide-react';
import { format } from 'date-fns';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { getRegionsForCountry } from '../../data/regions';
import IndustryMultiSelectFilter from '../IndustryMultiSelectFilter';
import { planGateFromResponse } from '../../lib/planGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const countries = [
  { code: 'SA', en: 'Saudi Arabia', ar: 'السعودية' },
  { code: 'AE', en: 'UAE', ar: 'الإمارات' },
  { code: 'OM', en: 'Oman', ar: 'عمان' },
  { code: 'KW', en: 'Kuwait', ar: 'الكويت' },
  { code: 'BH', en: 'Bahrain', ar: 'البحرين' },
  { code: 'QA', en: 'Qatar', ar: 'قطر' }
];

const statuses = [
  { value: 'open', en: 'Open', ar: 'مفتوح' },
  { value: 'closed', en: 'Closed', ar: 'مغلق' },
  { value: 'awarded', en: 'Awarded', ar: 'تم الترسية' },
  { value: 'cancelled', en: 'Cancelled', ar: 'ملغي' }
];

export function AdminTenderDialog({ open, onOpenChange, tender, token, language, onSuccess, userPlan = 'free', onPlanGate }) {
  const isEdit = !!tender;
  const isRTL = language === 'ar';
  const canSetPrivate = ['premium', 'enterprise'].includes(userPlan);
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title_en: '',
    title_ar: '',
    authority_en: '',
    authority_ar: '',
    country: 'SA',
    region: '',
    city: '',
    published_at: new Date(),
    deadline: null,
    sectors: [],
    status: 'open',
    source: 'Manual Entry',
    source_url: '',
    description: '',
    visibility: 'public',
    linked_project_ids: [],
    linked_company_ids: [],
    linked_news_ids: [],
  });

  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [news, setNews] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [newsSearch, setNewsSearch] = useState('');
  
  const [showPublishDatePicker, setShowPublishDatePicker] = useState(false);
  const [showDeadlinePicker, setShowDeadlinePicker] = useState(false);

  // Fetch linked entity lists when dialog opens
  useEffect(() => {
    if (!open) return;
    setLoadingLinked(true);
    Promise.all([
      fetch(`${BACKEND_URL}/api/intelligence/projects?page_size=100`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : { items: [] }).then(d => d.items || d.data || []),
      fetch(`${BACKEND_URL}/api/intelligence/companies?page_size=100`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : { items: [] }).then(d => d.items || d.data || []),
      fetch(`${BACKEND_URL}/api/intelligence/news?page_size=100`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : { items: [] }).then(d => d.items || d.data || []),
    ]).then(([p, c, n]) => {
      setProjects(p);
      setCompanies(c);
      setNews(n);
    }).catch(() => {}).finally(() => setLoadingLinked(false));
  }, [open, token]);

  useEffect(() => {
    if (tender) {
      setFormData({
        title_en: tender.title?.en || '',
        title_ar: tender.title?.ar || '',
        authority_en: tender.authority?.en || '',
        authority_ar: tender.authority?.ar || '',
        country: tender.country || 'SA',
        region: tender.region || '',
        city: tender.city || '',
        published_at: tender.published_at ? new Date(tender.published_at) : new Date(),
        deadline: tender.deadline ? new Date(tender.deadline) : null,
        sectors: tender.sectors?.length ? tender.sectors : (tender.sector ? [tender.sector] : []),
        status: tender.status || 'open',
        source: tender.source || 'Manual Entry',
        source_url: tender.source_url || '',
        description: tender.description || '',
        visibility: tender.visibility || 'public',
        linked_project_ids: tender.linked_project_ids || [],
        linked_company_ids: tender.linked_company_ids || [],
        linked_news_ids: tender.linked_news_ids || [],
      });
    } else {
      setFormData({
        title_en: '',
        title_ar: '',
        authority_en: '',
        authority_ar: '',
        country: 'SA',
        region: '',
        city: '',
        published_at: new Date(),
        deadline: null,
        sectors: [],
        status: 'open',
        source: 'Manual Entry',
        source_url: '',
        description: '',
        visibility: 'public',
        linked_project_ids: [],
        linked_company_ids: [],
        linked_news_ids: [],
      });
    }
    setProjectSearch('');
    setCompanySearch('');
    setNewsSearch('');
  }, [tender, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title_en.trim()) {
      toast.error(language === 'ar' ? 'العنوان (إنجليزي) مطلوب' : 'Title (English) is required');
      return;
    }

    // NEW-3: date validation — deadline must be after publication date.
    if (formData.deadline && formData.published_at && formData.deadline <= formData.published_at) {
      toast.error(language === 'ar'
        ? 'يجب أن يكون الموعد النهائي بعد تاريخ النشر'
        : 'Deadline must be after the publication date');
      return;
    }
    // Warn (but allow) if the deadline is already in the past for an open tender.
    if (formData.deadline && formData.deadline < new Date() && formData.status === 'open') {
      toast.error(language === 'ar'
        ? 'الموعد النهائي في الماضي — اضبط الحالة على "مغلقة" أو حدّث التاريخ'
        : 'Deadline is in the past — set status to "Closed" or update the date');
      return;
    }

    setLoading(true);
    
    try {
      const url = isEdit 
        ? `${BACKEND_URL}/api/admin/tenders/${pathId(tender._id)}`
        : `${BACKEND_URL}/api/admin/tenders`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      const payload = {
        ...formData,
        published_at: formData.published_at?.toISOString(),
        deadline: formData.deadline?.toISOString() || null,
        visibility: canSetPrivate ? formData.visibility : 'public'
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const pg = await planGateFromResponse(response);
        if (pg && onPlanGate) { onOpenChange(false); onPlanGate(pg); return; }
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to save tender');
      }
      
      toast.success(isEdit 
        ? (language === 'ar' ? 'تم تحديث المناقصة بنجاح' : 'Tender updated successfully')
        : (language === 'ar' ? 'تم إنشاء المناقصة بنجاح' : 'Tender created successfully')
      );
      
      onSuccess?.();
      onOpenChange(false);
      
    } catch (error) {
      console.error('Error saving tender:', error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const txt = {
    title: language === 'ar' ? (isEdit ? 'تعديل المناقصة' : 'إنشاء مناقصة جديدة') : (isEdit ? 'Edit Tender' : 'Create Tender'),
    subtitle: language === 'ar' ? 'أدخل تفاصيل المناقصة' : 'Enter tender details',
    titleEnLabel: 'Title',
    awardingEntity: 'Awarding Entity',
    country: 'Country',
    region: 'Region / Province',
    city: 'City',
    publishDate: 'Publish Date',
    deadline: 'Deadline',
    sector: 'Industry',
    status: 'Status',
    source: 'Source',
    sourceUrl: 'Source URL',
    description: 'Description',
    save: 'Save',
    cancel: 'Cancel',
    saving: 'Saving...',
    selectDate: 'Select date'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? <Pencil className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            {txt.title}
          </DialogTitle>
          <DialogDescription>{txt.subtitle}</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label>{txt.titleEnLabel} *</Label>
            <Input
              value={formData.title_en}
              onChange={(e) => setFormData(prev => ({ ...prev, title_en: e.target.value }))}
              placeholder="Enter tender title..."
              className="bg-background"
              dir="ltr"
            />
          </div>
          
          {/* Awarding Entity */}
          <div className="space-y-2">
            <Label>{txt.awardingEntity}</Label>
            <Input
              value={formData.authority_en}
              onChange={(e) => setFormData(prev => ({ ...prev, authority_en: e.target.value }))}
              placeholder="Ministry of..."
              className="bg-background"
              dir="ltr"
            />
          </div>

          {/* Country + Region */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{txt.country}</Label>
              <Select
                value={formData.country}
                onValueChange={(value) => setFormData(prev => ({ ...prev, country: value, region: '' }))}
              >
                <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {countries.map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{txt.region}</Label>
              {getRegionsForCountry(formData.country) ? (
                <Select
                  value={formData.region || ''}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, region: value }))}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="Select region..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getRegionsForCountry(formData.country).map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={formData.region || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
                  placeholder="Region / Province"
                  className="bg-background"
                />
              )}
            </div>
          </div>

          {/* City + Industry + Status */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{txt.city}</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                placeholder="Riyadh, Dubai..."
                className="bg-background"
                dir="ltr"
              />
            </div>

            <div className="space-y-2">
              <Label>{txt.sector}</Label>
              <IndustryMultiSelectFilter
                selected={formData.sectors || []}
                onChange={(vals) => setFormData(prev => ({ ...prev, sectors: vals }))}
                language={language}
                triggerClassName="w-full min-w-0 bg-background"
                popoverAlign="start"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{txt.status}</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statuses.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Dates Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{txt.publishDate}</Label>
              <Popover open={showPublishDatePicker} onOpenChange={setShowPublishDatePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal bg-background">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.published_at ? format(formData.published_at, 'PPP') : txt.selectDate}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.published_at}
                    onSelect={(date) => {
                      setFormData(prev => ({ ...prev, published_at: date || new Date() }));
                      setShowPublishDatePicker(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="space-y-2">
              <Label>{txt.deadline}</Label>
              <Popover open={showDeadlinePicker} onOpenChange={setShowDeadlinePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal bg-background">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.deadline ? format(formData.deadline, 'PPP') : txt.selectDate}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.deadline}
                    onSelect={(date) => {
                      setFormData(prev => ({ ...prev, deadline: date }));
                      setShowDeadlinePicker(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          
          {/* Source Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{txt.source}</Label>
              <Input
                value={formData.source}
                onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value }))}
                placeholder="Etimad Portal"
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label>{txt.sourceUrl}</Label>
              <Input
                value={formData.source_url}
                onChange={(e) => setFormData(prev => ({ ...prev, source_url: e.target.value }))}
                placeholder="https://..."
                className="bg-background"
                dir="ltr"
              />
            </div>
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <Label>{txt.description}</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter tender description..."
              className="bg-background"
              rows={3}
            />
          </div>
          
          {/* Linked Projects */}
          <div className="space-y-2 pt-2 border-t">
            <Label className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Linked Projects
            </Label>
            {formData.linked_project_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.linked_project_ids.map(pid => {
                  const proj = projects.find(p => p._id === pid);
                  return proj ? (
                    <Badge key={pid} variant="default" className="gap-1 bg-blue-500/20 text-blue-700 border-blue-300">
                      {proj.canonical_name}
                      <X className="w-3 h-3 cursor-pointer hover:text-destructive" onClick={() => setFormData(prev => ({ ...prev, linked_project_ids: prev.linked_project_ids.filter(id => id !== pid) }))} />
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)} placeholder="Search projects..." className="pl-9 bg-background" />
            </div>
            <div className="border rounded-md max-h-36 overflow-y-auto">
              {projectSearch.length === 0 ? (
                <div className="p-3 text-center text-muted-foreground text-sm">Type to search projects...</div>
              ) : loadingLinked ? (
                <div className="p-3 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
              ) : projects.filter(p => p.canonical_name?.toLowerCase().includes(projectSearch.toLowerCase())).length === 0 ? (
                <div className="p-3 text-center text-muted-foreground text-sm">No projects found</div>
              ) : projects.filter(p => p.canonical_name?.toLowerCase().includes(projectSearch.toLowerCase())).slice(0, 20).map(proj => (
                <div key={proj._id} className="flex items-center gap-3 p-2 hover:bg-muted/50 cursor-pointer border-b last:border-b-0"
                  onClick={() => setFormData(prev => ({ ...prev, linked_project_ids: prev.linked_project_ids.includes(proj._id) ? prev.linked_project_ids.filter(id => id !== proj._id) : [...prev.linked_project_ids, proj._id] }))}>
                  <Checkbox checked={formData.linked_project_ids.includes(proj._id)} onCheckedChange={() => {}} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{proj.canonical_name}</div>
                    {proj.country && <div className="text-xs text-muted-foreground">{proj.country}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Linked Companies */}
          <div className="space-y-2 pt-2 border-t">
            <Label className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Linked Companies
            </Label>
            {formData.linked_company_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.linked_company_ids.map(cid => {
                  const co = companies.find(c => c._id === cid);
                  return co ? (
                    <Badge key={cid} variant="default" className="gap-1 bg-purple-500/20 text-purple-700 border-purple-300">
                      {co.canonical_name}
                      <X className="w-3 h-3 cursor-pointer hover:text-destructive" onClick={() => setFormData(prev => ({ ...prev, linked_company_ids: prev.linked_company_ids.filter(id => id !== cid) }))} />
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} placeholder="Search companies..." className="pl-9 bg-background" />
            </div>
            <div className="border rounded-md max-h-36 overflow-y-auto">
              {companySearch.length === 0 ? (
                <div className="p-3 text-center text-muted-foreground text-sm">Type to search companies...</div>
              ) : loadingLinked ? (
                <div className="p-3 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
              ) : companies.filter(c => c.canonical_name?.toLowerCase().includes(companySearch.toLowerCase())).length === 0 ? (
                <div className="p-3 text-center text-muted-foreground text-sm">No companies found</div>
              ) : companies.filter(c => c.canonical_name?.toLowerCase().includes(companySearch.toLowerCase())).slice(0, 20).map(co => (
                <div key={co._id} className="flex items-center gap-3 p-2 hover:bg-muted/50 cursor-pointer border-b last:border-b-0"
                  onClick={() => setFormData(prev => ({ ...prev, linked_company_ids: prev.linked_company_ids.includes(co._id) ? prev.linked_company_ids.filter(id => id !== co._id) : [...prev.linked_company_ids, co._id] }))}>
                  <Checkbox checked={formData.linked_company_ids.includes(co._id)} onCheckedChange={() => {}} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{co.canonical_name}</div>
                    {co.country && <div className="text-xs text-muted-foreground">{co.country}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Linked News */}
          <div className="space-y-2 pt-2 border-t">
            <Label className="flex items-center gap-2">
              <Newspaper className="w-4 h-4" />
              Linked News
            </Label>
            {formData.linked_news_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.linked_news_ids.map(nid => {
                  const item = news.find(n => n._id === nid);
                  return item ? (
                    <Badge key={nid} variant="default" className="gap-1 bg-emerald-500/20 text-emerald-700 border-emerald-300 max-w-[260px]">
                      <span className="truncate">{item.title || item.title_en || 'News item'}</span>
                      <X className="w-3 h-3 cursor-pointer hover:text-destructive shrink-0" onClick={() => setFormData(prev => ({ ...prev, linked_news_ids: prev.linked_news_ids.filter(id => id !== nid) }))} />
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={newsSearch} onChange={(e) => setNewsSearch(e.target.value)} placeholder="Search news..." className="pl-9 bg-background" />
            </div>
            <div className="border rounded-md max-h-36 overflow-y-auto">
              {newsSearch.length === 0 ? (
                <div className="p-3 text-center text-muted-foreground text-sm">Type to search news...</div>
              ) : loadingLinked ? (
                <div className="p-3 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
              ) : news.filter(n => (n.title || n.title_en || '').toLowerCase().includes(newsSearch.toLowerCase())).length === 0 ? (
                <div className="p-3 text-center text-muted-foreground text-sm">No news found</div>
              ) : news.filter(n => (n.title || n.title_en || '').toLowerCase().includes(newsSearch.toLowerCase())).slice(0, 20).map(item => (
                <div key={item._id} className="flex items-center gap-3 p-2 hover:bg-muted/50 cursor-pointer border-b last:border-b-0"
                  onClick={() => setFormData(prev => ({ ...prev, linked_news_ids: prev.linked_news_ids.includes(item._id) ? prev.linked_news_ids.filter(id => id !== item._id) : [...prev.linked_news_ids, item._id] }))}>
                  <Checkbox checked={formData.linked_news_ids.includes(item._id)} onCheckedChange={() => {}} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{item.title || item.title_en || 'Untitled'}</div>
                    {item.published_at && <div className="text-xs text-muted-foreground">{new Date(item.published_at).toLocaleDateString()}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Visibility Toggle - Only for Premium/Enterprise users */}
          {canSetPrivate && (
            <div className="space-y-2 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="flex items-center gap-2">
                    {formData.visibility === 'private' ? (
                      <Lock className="w-4 h-4 text-amber-500" />
                    ) : (
                      <Globe className="w-4 h-4 text-emerald-500" />
                    )}
                    {language === 'ar' ? 'الرؤية' : 'Visibility'}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {formData.visibility === 'private' 
                      ? (language === 'ar' 
                          ? 'خاص - فقط من لديهم الرابط يمكنهم الوصول' 
                          : 'Private - Only people with the link can access')
                      : (language === 'ar' 
                          ? 'عام - مرئي للجميع' 
                          : 'Public - Visible to everyone')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {formData.visibility === 'private' 
                      ? (language === 'ar' ? 'خاص' : 'Private')
                      : (language === 'ar' ? 'عام' : 'Public')}
                  </span>
                  <Switch
                    checked={formData.visibility === 'private'}
                    onCheckedChange={(checked) => setFormData(prev => ({ 
                      ...prev, 
                      visibility: checked ? 'private' : 'public' 
                    }))}
                    data-testid="tender-visibility-toggle"
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              {txt.cancel}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {loading ? txt.saving : txt.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AdminTenderDialog;
